import { computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlEvent, FormControl, TouchedChangeEvent } from '@angular/forms';
import {
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  startWith,
  take,
} from 'rxjs';

/** Form validation errors for string form controls. */
export type FormErrors = {
  required?: boolean,
  minlength?: { requiredLength: number },
  maxlength?: { requiredLength: number },
  pattern?: {
    actualValue: string,
    requiredPattern: string
  },
};

/**
 * Creates a reactive signal for form control errors with intelligent visibility.
 *
 * @remarks
 * Combines multiple conditions to determine when to show errors:
 * - Touch tracking: Errors shown after user interaction
 * - Auto-focus handling: Errors shown immediately for auto-focused fields after first click
 * - Value changes: Errors updated when value changes (if dirty)
 * - Optional debouncing: Delays error display during typing
 *
 * This prevents showing errors on page load while ensuring immediate feedback
 * for auto-focused fields and responsive updates during user interaction.
 *
 * Options: autoFocused (show errors immediately for auto-focused fields),
 * inputDelay (debounce delay in ms, or true for default 300ms).
 *
 * @param control - Form control to track
 * @param options - Configuration options
 * @returns Signal with errors (null if shouldn't show)
 *
 * @example
 * ```typescript
 * // Basic usage
 * const errors = formControlErrors(emailControl);
 *
 * // Auto-focused field
 * const errors = formControlErrors(emailControl, { autoFocused: true });
 *
 * // With debounce
 * const errors = formControlErrors(passwordControl, { inputDelay: 500 });
 * ```
 */
export const formControlErrors: (
  control: FormControl<string>,
  options?: {
    autoFocused?: boolean;
    inputDelay?: boolean|number;
  }
) => Signal<FormErrors|null> = (
  control: FormControl<string>,
  options?: {
    autoFocused?: boolean;
    inputDelay?: boolean|number;
  }
) => {

  const firstClick$ = fromEvent<PointerEvent>(window, 'pointerup').
  pipe(
    map(() => true),
    startWith(false),
    filter(val => val === true),
    take(1)
  );

  const touched = toSignal(
    control.events
      .pipe(
        filter((event: ControlEvent) => event instanceof TouchedChangeEvent),
        map((event: TouchedChangeEvent) => event.touched),
        combineLatestWith(firstClick$),
        map(([touched, firstClick]) => {
          if (options?.autoFocused && firstClick) {
            return true;
          }
          return touched;
        }),
      ),
    { initialValue: control.touched },
  );

  const invalid = toSignal(
    control.statusChanges
      .pipe(
        filter((status) => status === 'INVALID'),
        map(() => true),
      ),
    { initialValue: control.status === 'INVALID' },
  );

  const value$ = control.valueChanges
      .pipe(
        filter(() => control.dirty),
        map(() => control.value),
      );
  if (options?.inputDelay) {
    const delay = typeof options.inputDelay === 'number' ? options.inputDelay : 300;
    value$.pipe(debounceTime(delay));
  }
  value$.pipe(distinctUntilChanged());

  const value = toSignal(value$, { initialValue: control.value })

  const showError = computed(() => {
    return touched() && invalid();
  });

  return computed(() => {
    // invoke value signal to ensure errors are updated if value changes
    value();

    if (!showError()) {
      return null;
    }
    return control.errors;
  });
};
